import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { Divider } from "@/components/shared/divider";
import { useBookingState } from "@/store/booking/useBookingState";
import type { ClientServiceProfileFragmentFragment } from "@/__generated__/graphql";
import {
  ClientServiceProfileFragmentFragmentDoc,
  PackageUnitTypeEnum,
} from "@/__generated__/graphql";
import {
  type PetProfileFormData,
  usePetProfileForm,
} from "@/containers/profile/service-profile/service-profile-form/pet/usePetProfileForm";
import { PetProfileForm } from "@/containers/profile/service-profile/service-profile-form/pet/form";
import { Button, View } from "@/components/ui";
import { useCreateClientServiceProfileMutation } from "@/gqlhooks/profile/useCreateClientServiceProfileMutation";
import { ServiceProfileCard } from "@/components/shared/service-profile/service-profile-card";
import { useClientServiceProfileByFiltersQuery } from "@/gqlhooks/profile/useClientServiceProfileByFiltersQuery";
import { CreateNewProfileCard } from "@/components/shared/service-profile/create-new-profile-card";
import { useEffect, useState } from "react";
import { useUpdateClientServiceProfileMutation } from "@/gqlhooks/profile/useUpdateClientServiceProfileMutation";
import {
  transformServiceProfile,
  transformServiceProfileToDetail,
} from "@/containers/profile/service-profile/lib";
import { LoadingDialog } from "@/components/shared/loading-dialog";
import { ClientInstance } from "@/lib/graphql/apollo";
import { Typography } from "@/components/shared/typography";
import { ListHeading } from "@/components/shared/list-header";
import { ClipboardText } from "@/components/shared/icons";
import { showToast } from "@/components/ui/toast/show-toast";
import { getFragmentData } from "@/__generated__";

export type PetProfileDetail = PetProfileFormData & { id: string };

export const setPetProfileInfo = (data: PetProfileDetail) => {
  useBookingState.getState().setBookingState({
    petProfileInfo: {
      ...data,
    },
    selectedServiceProfileId: data.id,
  });
};

export const PetProfileModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const [isCreating, setIsCreatingNew] = useState(false);
  const [editingProfile, setEditingProfile] = useState<PetProfileDetail | null>(
    null,
  );

  const handleClose = (closeAll = false) => {
    setIsCreatingNew(false);
    setEditingProfile(null);
    if (closeAll) {
      setOpen(false);
    }
  };

  const handleCreate = () => {
    setIsCreatingNew(true);
    setEditingProfile(null);
  };

  const handleEdit = (profile: PetProfileDetail) => {
    setEditingProfile(profile);
    setIsCreatingNew(false);
  };

  if (!open) {
    return null;
  }

  if (isCreating || editingProfile) {
    return (
      <FormPetProfileModal
        key={editingProfile?.id}
        editingProfile={editingProfile}
        handleClose={handleClose}
      />
    );
  }

  return (
    <ListPetProfileModal
      handleCreate={handleCreate}
      handleEdit={handleEdit}
      handleClose={() => handleClose(true)}
    />
  );
};

export const FormPetProfileModal = ({
  handleClose,
  editingProfile,
}: {
  handleClose: (closeAll?: boolean) => void;
  editingProfile: PetProfileDetail | null;
}) => {
  const packageDetailsFilter = useBookingState(
    ({ data }) => data.packageDetailsFilter,
  );
  const isDogPet =
    packageDetailsFilter?.unitTypes?.[0] === PackageUnitTypeEnum.Dog;

  const { control, handleSubmit, setValue } = usePetProfileForm(
    editingProfile ?? undefined,
  );

  const { createProfile, loading } = useCreateClientServiceProfileMutation();
  const [updateProfile, { loading: updateProfileLoading }] =
    useUpdateClientServiceProfileMutation();

  useEffect(() => {
    setValue("type", isDogPet ? "dog" : "cat");
  }, [isDogPet]);

  const onSave = async (data: PetProfileFormData) => {
    const { name, ...profileAttributes } = data;
    const transformedProfileAttributes = transformServiceProfile(
      "pet",
      profileAttributes,
    );

    let selectedServiceProfile: ClientServiceProfileFragmentFragment | null =
      null;
    if (editingProfile) {
      const res = await updateProfile({
        variables: {
          input: {
            id: editingProfile.id,
            name,
            profileAttributes: transformedProfileAttributes,
          },
        },
      });

      if (res.data?.updateClientServiceProfile) {
        selectedServiceProfile = getFragmentData(
          ClientServiceProfileFragmentFragmentDoc,
          res.data?.updateClientServiceProfile.clientServiceProfile,
        );
        showToast({
          title: "Profile updated successfully",
          type: "success",
        });
      }
    } else {
      const res = await createProfile({
        type: "pet",
        name: data.name,
        profileAttributes: transformedProfileAttributes,
        primary: true,
      });

      if (res.data?.createClientServiceProfile) {
        selectedServiceProfile = getFragmentData(
          ClientServiceProfileFragmentFragmentDoc,
          res.data?.createClientServiceProfile.clientServiceProfile,
        );
        showToast({
          title: "Profile created successfully",
          type: "success",
        });
      }
    }

    if (selectedServiceProfile) {
      setPetProfileInfo({
        id: selectedServiceProfile.id,
        ...transformServiceProfileToDetail("pet", selectedServiceProfile),
      });
    }
    handleClose(true);
  };

  return (
    <BottomDrawerModal open onOpenChange={handleClose}>
      <PetProfileForm control={control} readonlyType />
      <Divider className="my-2" />
      <Button variant="primary" color="CTA" onClick={handleSubmit(onSave)}>
        Save
      </Button>
      <LoadingDialog open={loading || updateProfileLoading} />
    </BottomDrawerModal>
  );
};

export const ListPetProfileModal = ({
  handleCreate,
  handleEdit,
  handleClose,
}: {
  handleCreate: () => void;
  handleEdit: (profile: PetProfileDetail) => void;
  handleClose: () => void;
}) => {
  const { profiles } = useClientServiceProfileByFiltersQuery("pet");
  const selectedServiceProfileId = useBookingState(
    ({ data }) => data.selectedServiceProfileId,
  );
  const petType = useBookingState(({ data }) =>
    data.packageDetailsFilter?.unitTypes?.[0] === PackageUnitTypeEnum.Dog
      ? "dog"
      : "cat",
  );

  const _handleEdit = (profileId: string) => {
    const profile = ClientInstance.readFragment({
      id: `ClientServiceProfile:${profileId}`,
      fragment: ClientServiceProfileFragmentFragmentDoc,
    });
    if (profile) {
      handleEdit({
        id: profile.id,
        ...transformServiceProfileToDetail("pet", profile),
      });
    }
  };

  const _setPetProfileInfo = (profileId: string) => {
    const profile = ClientInstance.readFragment({
      id: `ClientServiceProfile:${profileId}`,
      fragment: ClientServiceProfileFragmentFragmentDoc,
    });
    if (profile) {
      setPetProfileInfo({
        id: profileId,
        ...transformServiceProfileToDetail("pet", profile),
      });
    }
    handleClose();
  };

  const profilesToShow = profiles.filter(
    (profile) => profile.visibleAttributes[0].toLowerCase() === petType,
  );

  const canCreateNewProfile = () => {
    const catCount = profiles.filter(
      (profile) => profile.visibleAttributes[0] === "Cat",
    ).length;
    const dogCount = profiles.filter(
      (profile) => profile.visibleAttributes[0] === "Dog",
    ).length;
    if (petType === "dog") {
      return dogCount < 3;
    }
    return catCount < 3;
  };

  return (
    <BottomDrawerModal open onOpenChange={handleClose}>
      <ListHeading title="Choose a Pet Profile" icon={ClipboardText} />
      <Typography
        color="foreground-intermediate"
        variant="body-md"
        className="pt-1"
      >
        Share a few details so our groomers can prep the perfect session for
        your furry friend
      </Typography>
      <Divider className="my-2" />
      <View className="flex flex-col gap-3">
        {profilesToShow.map((profile) => (
          <ServiceProfileCard
            key={profile.id}
            profile={profile}
            selected={profile.id === selectedServiceProfileId}
            onClick={() => _setPetProfileInfo(profile.id)}
            handleEdit={() => _handleEdit(profile.id)}
          />
        ))}
        {canCreateNewProfile() && (
          <CreateNewProfileCard onClick={() => handleCreate()} />
        )}
      </View>
    </BottomDrawerModal>
  );
};
